/**
 * Transaction engine types.
 *
 * Relocated from frontend/src/lib/tx-engine/types.ts as part of the auth
 * boundary migration (spec 009 FILE 14 Group E — `useSubmitTransaction`).
 *
 * Platform-neutral: pure type declarations. Safe in src/auth/ core per
 * .claude/rules/platform-agnostic.md.
 */

import type { SigningDeps } from '../auth/turnkey/signing-types.js';
import type { TxConnectionConfig } from './config.js';
import type { TurnkeyDeps } from './turnkey-types.js';

/** Wallet type classification for signing dispatch. */
export type WalletType = 'embedded' | 'imported' | 'external';

/** Active wallet info needed by the engine. */
export interface ActiveWallet {
  id: string;
  address: string;
  type: WalletType;
}

/** Jito configuration for external wallets on mainnet. */
export interface JitoConfig {
  tipLamports: number;
}

/** Transaction lifecycle stages. */
export type TxStage =
  | 'preflight'
  | 'building'
  | 'signing'
  | 'relaying'
  | 'confirming'
  | 'confirmed'
  | 'failed'
  | 'cancelled';

/** Landing (confirmation) configuration with sensible defaults. */
export interface LandingConfig {
  maxRetries: number;
  confirmationTimeoutMs: number;
  commitment: 'processed' | 'confirmed' | 'finalized';
}

export const DEFAULT_LANDING_CONFIG: LandingConfig = {
  maxRetries: 3,
  confirmationTimeoutMs: 60_000,
  commitment: 'confirmed',
};

export interface SubmissionRetryContext {
  readonly workflow: 'market_creation' | 'add_outcome' | 'claim_prize';
  readonly workflowTraceId: string;
}

export type SubmissionAttemptContext = SubmissionRetryContext &
  (
    | { readonly initialTraceId?: never; readonly retryAttempt?: never }
    | { readonly initialTraceId: string; readonly retryAttempt: number }
  );

/** Options passed to submitTransaction / executePipeline. */
export interface SubmissionOptions {
  instructionName: string;
  /** Shared by the legs of one product trade; captures the first submission's network. */
  connectionScope?: { config?: TxConnectionConfig };
  /**
   * Optional caller-supplied trace id. The trade hooks pass their mutation
   * traceId so the tx-engine logs + the NATS early-confirm line share ONE
   * trace per trade. Must be a ULID accepted by backend correlation middleware;
   * invalid or omitted values are replaced with a fresh ULID.
   */
  traceId?: string;
  signingDeps: SigningDeps;
  /**
   * Turnkey submission capabilities (sol send, status query, gas usage,
   * activity poller). Always present in the option bag because the
   * providing hook (`useTurnkeySubmissionDeps`) is auth-layer-singleton and
   * resolves regardless of wallet type. Consumed only by the Turnkey
   * branch — external-wallet (`relay-primary`) submissions tolerate an
   * inert TurnkeyDeps shape per data-model.md.
   */
  turnkeyDeps: TurnkeyDeps;
  activeWallet: ActiveWallet;
  jitoConfig?: JitoConfig;
  landingConfig?: Partial<LandingConfig>;
  signal?: AbortSignal;
  slippageBps?: number;
  suggestedSlippageBps?: number;
  marketPhase?: 'bonding_curve' | 'amm';
  /** Caller-supplied log metadata for correlating a tx with a higher-level flow. */
  metadata?: Readonly<Record<string, string | number | boolean>>;
  /** Typed lifecycle correlation set by the market-creation retry wrapper. */
  attemptContext?: SubmissionAttemptContext | undefined;
  /**
   * base58 program id used to attribute custom-error codes when the failure
   * has no program log line (the no-logs confirmation path). Graduated-AMM
   * swap hooks pass the cp-swap id so its 6000+ codes don't collide with
   * the markets program's. See `extractMorfiErrorName`.
   */
  programIdHint?: string;
  /**
   * Force embedded/imported wallets through the Morfi relay even when the
   * transaction has no caller-owned presigners. Used by sponsored market
   * creation so the relay fee payer can also be an instruction-level rent payer.
   */
  forceRelay?: boolean;
  /** Reserve an operation after wallet signing, immediately before relay submission. */
  beforeBroadcast?: () => Promise<void>;
  /**
   * Relay fee payer that the caller already embedded in instruction accounts.
   * The tx engine still resolves the current relay payer from `/v1/config/relay`;
   * if it differs from this value, submission aborts before signing/relaying.
   */
  expectedRelayFeePayer?: string;
  /**
   * Caller-owned signers that must sign the tx BEFORE the user (Turnkey)
   * signature is added. Used for instructions whose IDL declares additional
   * `isSigner=true` accounts that aren't the user's wallet — e.g.
   * an instruction may require a caller-owned Keypair alongside the user.
   * Pre-signing happens client-side; the orchestrator
   * generates the Keypair via `Keypair.generate()`, holds its secret, and
   * passes it here.
   *
   * `VersionedTransaction.sign(keypairs)` is a set-like operation — passing
   * the keypair adds its signature to the corresponding slot and leaves
   * other signature slots untouched. The Turnkey sign step is therefore
   * still able to add the user's signature; the relay still adds the
   * fee-payer signature; the submitted tx ends up with all three.
   *
   * Typed as `unknown[]` here to avoid forcing @solana/web3.js into the
   * platform-neutral type layer; the pipeline narrows to `Keypair[]` at
   * the use site.
   */
  presignKeypairs?: ReadonlyArray<unknown>;
}

/**
 * Discriminator for which submission path executed. Pure function of
 * wallet, cap, force-relay, and RPC endpoint state — see router.ts.
 * Recorded on every telemetry event as `tx_path`.
 */
export type TxPath = 'turnkey-sponsored' | 'relay-failover' | 'relay-primary';

/** Snapshot recorded at failover-trigger time. */
export interface CapUtilization {
  usageUsd: number;
  windowLimitUsd: number;
  windowDurationMinutes: number;
  remainingUsd: number;
  pctUsed: number;
  measuredAt: number;
}

/**
 * Result of the pre-flight cap check. Numeric fields are parsed at the
 * `getGasUsage` boundary (Turnkey returns strings) and clamped/guarded
 * for `limit === 0` and parse failures.
 */
export type CapStatus =
  | { status: 'available'; remainingUsd: number; pctUsed: number }
  | {
      status: 'exhausted';
      usageUsd: number;
      windowLimitUsd: number;
      windowDurationMinutes: number;
    }
  | {
      status: 'unknown';
      reason: 'network_error' | 'timeout' | 'http_error' | 'parse_failure';
      httpStatus?: number;
    };

/**
 * Lifecycle state of a Turnkey `solSendTransaction` activity. Turnkey
 * publishes `txStatus` as a free-form string; we narrow where possible
 * and treat unknown values as "keep polling".
 */
export type ActivityStatus = 'COMPLETED' | 'INCLUDED' | 'FAILED' | 'PENDING' | 'BROADCASTING';

/** Structured relay response from backend. */
export interface RelayResponse {
  signature: string;
  blockhash: string;
  lastValidBlockHeight: number;
  requestId?: string;
  httpStatus?: number;
}

/** Structured relay error response from backend. */
export interface RelayErrorResponse {
  error: string;
  message: string;
  simulationLogs?: string[];
  retryAfterSeconds?: number;
  currentBalance?: number;
}

/** Recovery action union for error handling. */
export type RecoveryAction =
  | { type: 'not_retryable' }
  | { type: 'prompt_retry' }
  | { type: 'prompt_retry_with_higher_slippage'; suggestedSlippageBps: number }
  | { type: 'auto_retry' }
  | { type: 'auto_retry_after_delay'; retryAfterSeconds: number }
  | { type: 'informational' };

/** Transaction error with code, stage, and recovery metadata. */
export interface TxErrorInfo {
  code: string;
  stage: TxStage;
  userMessageKey: string;
  traceId: string;
  recoveryAction: RecoveryAction;
  details?: Record<string, unknown> | undefined;
  morfiError?: string | undefined;
}

/** Submission result -- ok: true means on-chain confirmed. */
export type SubmissionResult =
  | { ok: true; signature: string; traceId: string }
  | { ok: false; error: TxErrorInfo; traceId: string };

/** Cached blockhash with metadata. */
export interface CachedBlockhash {
  blockhash: string;
  lastValidBlockHeight: number;
  fetchedAt: number;
}

/** Cached priority fee with metadata. */
export interface CachedPriorityFee {
  feeMicrolamports: number;
  network: string;
  fetchedAt: number;
}

/** Preflight result from parallel operations. */
export interface PreflightResult {
  priorityFee: number;
  blockhash: string;
  lastValidBlockHeight: number;
  simulationError?: TxErrorInfo | undefined;
}

/** Simulation result from the simulation module. */
export interface SimulationResult {
  error: TxErrorInfo | null;
  logs: string[];
}

/** Confirmation result from the confirmation module. */
export type ConfirmationResult =
  | { status: 'confirmed'; slot: number }
  | { status: 'failed'; error: TxErrorInfo }
  | { status: 'expired' }
  | { status: 'timeout' }
  | { status: 'cancelled' };

/** WebSocket health state. */
export type WsHealthState = 'connected' | 'disconnected' | 'reconnecting';
